// frontend/src/pages/Signup.js

import React, { memo, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "phosphor-react";
import { Preferences } from "@capacitor/preferences";

import { api } from "../api";
import PreLoginHeader from "../components/PreLoginHeader";

const OceanSparkleBackground = memo(function OceanSparkleBackground() {
  return (
    <div aria-hidden="true" className="ocean-background">
      <div className="ocean-light" />

      <div className="ocean-wave ocean-wave-one" />
      <div className="ocean-wave ocean-wave-two" />
      <div className="ocean-wave ocean-wave-three" />

      <div className="ocean-clouds">
        <div className="ocean-cloud ocean-cloud-one" />
        <div className="ocean-cloud ocean-cloud-two" />
        <div className="ocean-cloud ocean-cloud-three" />
        <div className="ocean-cloud ocean-cloud-four" />
        <div className="ocean-cloud ocean-cloud-five" />
      </div>

      <div className="ocean-wash" />
    </div>
  );
});

const DesktopBackgroundText = memo(function DesktopBackgroundText() {
  return (
    <div
      aria-hidden="true"
      className="
        pointer-events-none absolute inset-0 z-[2]
        hidden select-none overflow-hidden lg:block
      "
    >
      <p
        className="
          absolute -left-2 top-10 max-w-[390px]
          text-[78px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-50
          xl:text-[92px]
        "
      >
        where
        <br />
        every day
        <br />
        is a win
      </p>

      <p
        className="
          absolute right-[9%] top-[54%] max-w-[500px]
          text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        find your
        <br />
        community
      </p>

      <p
        className="
          absolute right-0 top-[30%] max-w-[590px]
          text-right text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        do more of
        <br />
        what you love
      </p>

      <p
        className="
          absolute bottom-[8%] right-0 max-w-[530px]
          text-right text-[66px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        celebrate your
        <br />
        every step
      </p>
    </div>
  );
});

function Signup() {
  const nameRef = useRef(null);
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const [bio, setBio] = useState("");

  const [errorMsg, setErrorMsg] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [makeProfilePublic, setMakeProfilePublic] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const checkCanSubmit = useCallback(() => {
    const hasName = Boolean(nameRef.current?.value.trim());
    const hasUsername = Boolean(
      usernameRef.current?.value.trim()
    );
    const hasEmail = Boolean(emailRef.current?.value.trim());
    const hasPassword = Boolean(
      passwordRef.current?.value.trim()
    );

    setCanSubmit((previousValue) => {
      const nextValue =
        hasName &&
        hasUsername &&
        hasEmail &&
        hasPassword;

      return previousValue === nextValue
        ? previousValue
        : nextValue;
    });
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((previousValue) => !previousValue);
  }, []);

  const handleSignup = async (event) => {
    event.preventDefault();

    setErrorMsg("");
    setShowForgot(false);
    setLoading(true);

    const name = nameRef.current?.value.trim() || "";

    const username = (
      usernameRef.current?.value.trim() || ""
    )
      .toLowerCase()
      .replace(/^@/, "");

    const email = emailRef.current?.value.trim() || "";
    const password = passwordRef.current?.value || "";
    const bioClean = bio.trim();

    if (!/^[a-z0-9._]{3,30}$/.test(username)) {
      setErrorMsg(
        "Username must be 3–30 characters using lowercase letters, numbers, periods, or underscores."
      );
      setLoading(false);
      return;
    }

    if (bioClean.length > 240) {
      setErrorMsg("Bio cannot exceed 240 characters.");
      setLoading(false);
      return;
    }

    if (!email.includes("@") || !email.includes(".")) {
      setErrorMsg("Invalid email. Please check your input.");
      setLoading(false);
      return;
    }

    try {
      await api.post("/users/", {
        name,
        username,
        bio: bioClean,
        email,
        password,
        is_profile_private: !makeProfilePublic,
        default_activity_private: !makeProfilePublic,
      });

      const loginBody = new URLSearchParams();

      loginBody.set("username", email);
      loginBody.set("password", password);

      const { data: loginData } = await api.post(
        "/auth/login",
        loginBody,
        {
          headers: {
            "Content-Type":
              "application/x-www-form-urlencoded",
          },
          timeout: 20000,
        }
      );

      const token =
        loginData?.access_token;

      const tokenType =
        (
          loginData?.token_type ||
          "Bearer"
        ).trim();

      if (!token) {
        throw new Error(
          "Account created, but automatic sign in failed."
        );
      }

      /*
      * Web auth storage
      */
      localStorage.setItem(
        "token",
        token
      );

      /*
      * Native / Capacitor auth storage
      */
      try {
        await Preferences.set({
          key: "auth_token",
          value: token,
        });
      } catch (storageError) {
        console.warn(
          "Could not save auth token to Preferences:",
          storageError
        );
      }

      let userId =
        loginData?.user?.id;

      if (!userId) {
        try {
          const meResponse =
            await api.get(
              "/users/me",
              {
                headers: {
                  Authorization:
                    `${tokenType} ${token}`,
                },
              }
            );

          userId =
            meResponse?.data?.id;
        } catch (meError) {
          console.warn(
            "Could not resolve new user id:",
            meError
          );
        }
      }

      if (userId) {
        localStorage.setItem(
          "user_id",
          String(userId)
        );

        try {
          await Preferences.set({
            key: "user_id",
            value: String(userId),
          });
        } catch {
          // localStorage fallback is enough for web.
        }
      }

      /*
      * User is authenticated now
      */
      navigate("/home", {
        replace: true,
      });
    } catch (error) {

      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Sign up failed - please check your information";

      const normalizedDetail =
        typeof detail === "string"
          ? detail.toLowerCase()
          : "";

      const isDuplicateEmail =
        (error?.response?.status === 400 ||
          error?.response?.status === 409) &&
        (normalizedDetail.includes("email already registered") ||
          normalizedDetail.includes("email already exists"));

      const isDuplicateUsername =
        (error?.response?.status === 400 ||
          error?.response?.status === 409) &&
        normalizedDetail.includes("username is already taken");

      if (isDuplicateEmail) {
        setErrorMsg(
          "An account with this email already exists"
        );
        setShowForgot(true);
      } else if (isDuplicateUsername) {
        setErrorMsg("Username is already taken");
      } else {
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : "Sign up failed. Please check your information."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      <section className="relative min-h-screen overflow-hidden">
        <OceanSparkleBackground />
        <PreLoginHeader />
        <DesktopBackgroundText />

        <div
          className="
            relative z-10 flex min-h-screen
            items-center justify-center
            px-6 pb-10 sm:pt-24 pt-20
          "
        >
          <main className="flex w-full max-w-[400px] flex-col items-center">
            <h1
              className="
                logo-font mb-6 select-none
                text-[50px] font-black leading-[0.9]
                tracking-[-0.06em] text-blue-500
                drop-shadow-[0_2px_14px_rgba(255,255,255,0.85)]
                sm:mb-8 sm:text-[72px]
              "
            >
              third place
            </h1>

            <div
              className="
                w-full max-w-[390px] 
                rounded-[32px] bg-slate-50/80
                px-4 py-5
                shadow-[0_24px_70px_rgba(22,59,86,0.04)]
                sm:p-5
              "
            >
              <form className="space-y-2" onSubmit={handleSignup}>
                {errorMsg && (
                  <div
                    className="
                      -mt-2
                      px-3 pt-1 text-left font-medium
                      text-sm text-red-600
                    "
                    aria-live="polite"
                  >
                    <span>{errorMsg}</span>

                    {showForgot && (
                      <button
                        type="button"
                        onClick={() => navigate("/forgotpassword")}
                        className="
                          ml-1 !min-h-0 !min-w-0
                          font-semibold text-blue-400
                          transition hover:font-bold
                        "
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="signup-name"
                    className="
                      mb-1.5 ml-3 block
                      text-sm font-semibold text-[#6f665d]
                    "
                  >
                    Display Name
                  </label>

                  <input
                    id="signup-name"
                    ref={nameRef}
                    type="text"
                    name="name"
                    required
                    autoComplete="name"
                    placeholder="Jade Kim"
                    onInput={checkCanSubmit}
                    onChange={checkCanSubmit}
                    onFocus={checkCanSubmit}
                    className="
                      w-full rounded-3xl
                      border border-white/80 bg-blue-50/85
                      !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500 focus:border-2
                    "
                  />
                </div>

                <div>
                  <label
                    htmlFor="signup-username"
                    className="
                      mb-1.5 ml-3 block
                      text-sm font-semibold text-[#6f665d]
                    "
                  >
                    Username
                  </label>

                  <div className="relative">
                    <span
                      className="
                        pointer-events-none absolute
                        left-3 top-1/2 -translate-y-1/2
                        text-sm text-slate-400
                      "
                    >
                      @
                    </span>

                    <input
                      id="signup-username"
                      ref={usernameRef}
                      type="text"
                      name="username"
                      required
                      minLength={3}
                      maxLength={30}
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="username"
                      onInput={(event) => {
                        event.currentTarget.value =
                          event.currentTarget.value
                            .toLowerCase()
                            .replace(/^@/, "")
                            .replace(/[^a-z0-9._]/g, "");

                        checkCanSubmit();
                      }}
                      onChange={checkCanSubmit}
                      onFocus={checkCanSubmit}
                      className="
                        w-full rounded-3xl
                        border border-white/80 bg-blue-50/85
                        !min-h-0 !min-w-0 !px-3 ml-0.5 !py-1.5 !pl-6 !pr-3 text-sm outline-none
                        placeholder:text-slate-400
                        focus:border-blue-500 focus:border-2
                      "
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="signup-email"
                    className="
                      mb-1.5 ml-3 block
                      text-sm font-semibold text-[#6f665d]
                    "
                  >
                    Email
                  </label>

                  <input
                    id="signup-email"
                    ref={emailRef}
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="you@example.com"
                    onInput={checkCanSubmit}
                    onChange={checkCanSubmit}
                    onFocus={checkCanSubmit}
                    className="
                      w-full rounded-3xl
                      border border-white/80 bg-blue-50/85
                      !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500 focus:border-2
                    "
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label
                      htmlFor="signup-bio"
                      className="
                        ml-3 block
                        text-sm font-semibold text-[#6f665d]
                      "
                    >
                      Bio
                    </label>

                    <span className="mr-4 text-xs text-[#8a8078]">
                      {bio.length}/240
                    </span>
                  </div>

                  <textarea
                    id="signup-bio"
                    name="bio"
                    value={bio}
                    onChange={(event) =>
                      setBio(event.target.value)
                    }
                    maxLength={240}
                    rows={3}
                    placeholder="Tell people a little about yourself"
                    className="
                      w-full rounded-2xl
                      border border-white/80 bg-blue-50/85
                      !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500 focus:border-2
                    "
                  />
                </div>

                <div>
                  <label
                    htmlFor="signup-password"
                    className="
                      mb-1.5 ml-3 block
                      text-sm font-semibold text-[#6f665d]
                    "
                  >
                    Password
                  </label>

                  <input
                    id="signup-password"
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    name="new-password"
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                    onInput={checkCanSubmit}
                    onChange={checkCanSubmit}
                    onFocus={checkCanSubmit}
                    className="
                      w-full rounded-3xl
                      border border-white/80 bg-blue-50/85
                      !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500 focus:border-2
                    "
                  />
                </div>

                <label
                  className="
                    inline-flex cursor-pointer
                    items-center px-2
                    text-sm text-[#6f665d]
                  "
                >
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={toggleShowPassword}
                    className="peer sr-only"
                  />

                  <div
                    className={`
                      flex !min-h-0 !min-w-0 !h-4 !w-4  shrink-0
                      items-center justify-center
                      rounded-full border
                      transition-colors
                      ${
                        showPassword
                          ? "border-blue-500 bg-blue-500"
                          : "border-slate-200 bg-blue-50"
                      }
                    `}
                  >
                    <Check
                      size={12}
                      weight="bold"
                      className={`
                        text-white transition-opacity !min-h-0 !min-w-0
                        ${
                          showPassword
                            ? "opacity-100"
                            : "opacity-0"
                        }
                      `}
                    />
                  </div>

                  <span className="ml-2 ">Show password</span>
                </label>

                <label
                  className="
                    flex cursor-pointer items-start gap-2
                    rounded-3xl
                    !min-h-0 !min-h-0 px-2 pb-1
                    text-left text-sm text-[#6f665d]
                  "
                >
                  <input
                    type="checkbox"
                    checked={makeProfilePublic}
                    onChange={(event) =>
                      setMakeProfilePublic(event.target.checked)
                    }
                    className="peer sr-only"
                  />

                  <div
                    className={`
                      mt-0.5 flex h-4 w-4 shrink-0
                      items-center justify-center
                      rounded-full border
                      transition-colors
                      ${
                        makeProfilePublic
                          ? "border-blue-500 bg-blue-500"
                          : "border-slate-200 bg-blue-50"
                      }
                    `}
                  >
                    <Check
                      size={12}
                      weight="bold"
                      className={`
                        text-white transition-opacity
                        ${
                          makeProfilePublic
                            ? "opacity-100"
                            : "opacity-0"
                        }
                      `}
                    />
                  </div>

                  <span>
                    Make my profile and activities public

                    <span
                      className="
                        mt-0.5 block
                        text-xs leading-tight text-[#8a8078]
                      "
                    >
                      Your account is private by default <br /> You can change this
                      later.
                    </span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading || !canSubmit}
                  className="
                    w-full transform-gpu rounded-full
                    bg-blue-600 !min-h-0 !min-w-0 !py-3
                    text-sm font-semibold text-white
                    transition-all duration-300 ease-out
                    hover:scale-[1.03]
                    hover:bg-blue-700
                    active:scale-[0.99]
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    disabled:hover:scale-100
                    disabled:hover:bg-blue-600
                  "
                >
                  {loading ? "creating account…" : "Join"}
                </button>

                <div className="text-center text-xs text-[#8a8078] !mt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="
                      !min-h-0 !min-w-0
                      font-semibold text-blue-400
                      transition hover:font-bold
                    "
                  >
                    Sign in
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/forgotpassword")}
                    className="
                      ml-3 !min-h-0 !min-w-0
                      font-medium text-blue-400
                      transition hover:font-bold
                    "
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
}

export default Signup;
